import { getYear, getMonth, getDaysInMonth, getDate, addMonths, addYears, addDays } from 'date-fns';

import {
    AfterContentInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    output,
    viewChild,
    input,
    inject
} from '@angular/core';

import { ApDateService } from '@ap/date';

import { ApCalendarBodyComponent, ApCalendarCell } from '../body/calendar-body.component';

/**
 * An internal component used to display a single year in the datepicker.
 *
 * @docs-private
 */
@Component({
    selector: 'ap-year-view',
    templateUrl: './year-view.component.html',
    styleUrls: ['../calendar.component.scss'],
    exportAs: 'apYearView',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ApCalendarBodyComponent]
})
export class ApYearViewComponent implements AfterContentInit {
    private readonly changeDetectorRef = inject(ChangeDetectorRef);

    /** The date to display in this year view (everything other than the year is ignored). */
    @Input()
    get activeDate(): Date {
        return this._activeDate;
    }

    set activeDate(value: Date) {
        const oldActiveDate = this._activeDate;

        const validDate = ApDateService.getValidDateOrNull(ApDateService.utc(value)) || ApDateService.utc();
        this._activeDate = ApDateService.clampDate(validDate, this.minDate(), this.maxDate());

        if (getYear(oldActiveDate) !== getYear(this._activeDate)) {
            this._init();
            this.activeDateChange.emit(this._activeDate);
        }
    }

    /** The currently selected date. */
    @Input()
    get selected(): Date | null {
        return this._selected;
    }

    set selected(value: Date | null) {
        this._selected = ApDateService.getValidDateOrNull(ApDateService.utc(value));
        this._selectedMonth = this._getMonthInCurrentYear(this._selected);
    }

    /** The minimum selectable date. */
    public readonly minDate = input(undefined, {
        transform: (value: Date | null): Date | null => {
            return ApDateService.getValidDateOrNull(ApDateService.utc(value));
        }
    });

    /** The maximum selectable date. */
    public readonly maxDate = input(undefined, {
        transform: (value: Date | null): Date | null => {
            return ApDateService.getValidDateOrNull(ApDateService.utc(value));
        }
    });

    /** A function used to filter which dates are selectable. */
    readonly dateFilter = input<(date: Date) => boolean>(undefined);
    /** Emits when a new month is selected. */
    readonly selectedChange = output<Date>();
    /** Emits the selected month. This doesn't imply a change on the selected date */
    readonly monthSelected = output<Date>();
    /** Emits when any date is activated. */
    readonly activeDateChange = output<Date>();
    /** The body of calendar table */
    readonly _apCalendarBody = viewChild(ApCalendarBodyComponent);
    /** Grid of calendar cells representing the months of the year. */
    _months: ApCalendarCell[][];
    /** The label for this year (e.g. "2017"). */
    _yearLabel: string;
    /** The month in this year that today falls on. Null if today is in a different year. */
    _todayMonth: number | null;
    /**
     * The month in this year that the selected Date falls on.
     * Null if the selected Date is in a different year.
     */
    _selectedMonth: number | null;

    private _activeDate: Date;

    private _selected: Date | null;

    private readonly monthNames = ['jan.', 'feb.', 'mrt.', 'apr.', 'mei', 'jun.', 'jul.', 'aug.', 'sep.', 'okt.', 'nov.', 'dec.'];

    public getMonth = getMonth;

    public constructor() {
        this._activeDate = ApDateService.utc();
    }

    public ngAfterContentInit(): void {
        this._init();
    }

    /** Handles when a new month is selected. */
    _monthSelected(month: number) {
        const normalizedDate = ApDateService.utc(new Date(getYear(this.activeDate), month, 1));

        this.monthSelected.emit(normalizedDate);

        const daysInMonth = getDaysInMonth(normalizedDate);

        this.selectedChange.emit(
            ApDateService.utc(new Date(getYear(this.activeDate), month, Math.min(getDate(this.activeDate), daysInMonth)))
        );
    }

    /** Handles keydown events on the calendar body when calendar is in year view. */
    _handleCalendarBodyKeydown(event: KeyboardEvent): void {
        // TODO(mmalerba): We currently allow keyboard navigation to disabled dates, but just prevent
        // disabled ones from being selected. This may not be ideal, we should look into whether
        // navigation should skip over disabled dates, and if so, how to implement that efficiently.

        // https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/code
        // tslint:disable-next-line:deprecation
        switch (event.key) {
            case 'ArrowLeft':
                this.activeDate = addMonths(this._activeDate, -1);
                break;
            case 'ArrowRight':
                this.activeDate = addMonths(this._activeDate, 1);
                break;
            case 'ArrowUp':
                this.activeDate = addMonths(this._activeDate, -4);
                break;
            case 'ArrowDown':
                this.activeDate = addMonths(this._activeDate, 4);
                break;
            case 'Home':
                this.activeDate = addMonths(this._activeDate, -getMonth(this._activeDate));
                break;
            case 'End':
                this.activeDate = addMonths(this._activeDate, 11 - getMonth(this._activeDate));
                break;
            case 'PageUp':
                this.activeDate = addYears(this._activeDate, event.altKey ? -10 : -1);
                break;
            case 'PageDown':
                this.activeDate = addYears(this._activeDate, event.altKey ? 10 : 1);
                break;
            case 'Enter':
                this._monthSelected(getMonth(this._activeDate));
                break;
            default:
                // Don't prevent default or focus active cell on keys that we don't explicitly handle.
                return;
        }

        this._focusActiveCell();
        // Prevent unexpected default actions such as form submission.
        event.preventDefault();
    }

    /** Initializes this year view. */
    _init(): void {
        this._selectedMonth = this._getMonthInCurrentYear(this.selected);
        this._todayMonth = this._getMonthInCurrentYear(ApDateService.utc());
        this._yearLabel = ApDateService.format(this.activeDate, 'yyyy');

        // First row of months only contains 5 elements so we can fit the year label on the same row.
        this._months = [
            [0, 1, 2, 3],
            [4, 5, 6, 7],
            [8, 9, 10, 11]
        ].map((row) => row.map((month) => this._createCellForMonth(month, this.monthNames[month])));
        this.changeDetectorRef.markForCheck();
    }

    /** Focuses the active cell after the microtask queue is empty. */
    _focusActiveCell(): void {
        this._apCalendarBody()._focusActiveCell();
    }

    /**
     * Gets the month in this year that the given Date falls on.
     * Returns null if the given Date is in another year.
     */
    private _getMonthInCurrentYear(date: Date | null): number | null {
        return date && getYear(date) === getYear(this.activeDate) ? getMonth(date) : null;
    }

    /** Creates an ApCalendarCell for the given month. */
    private _createCellForMonth(month: number, monthName: string): ApCalendarCell {
        const ariaLabel = ApDateService.format(ApDateService.utc(new Date(getYear(this.activeDate), month, 1)), 'MMMM yyyy');
        return new ApCalendarCell(month, monthName.toLocaleUpperCase(), ariaLabel, this._shouldEnableMonth(month));
    }

    /** Whether the given month is enabled. */
    private _shouldEnableMonth(month: number): boolean {
        const activeYear = getYear(this.activeDate);

        if (
            month === undefined ||
            month === null ||
            this._isYearAndMonthAfterMaxDate(activeYear, month) ||
            this._isYearAndMonthBeforeMinDate(activeYear, month)
        ) {
            return false;
        }

        const dateFilter = this.dateFilter();
        if (!dateFilter) {
            return true;
        }

        const firstOfMonth = ApDateService.utc(new Date(activeYear, month, 1));

        // If any date in the month is enabled count the month as enabled.
        for (let date = firstOfMonth; getMonth(date) === month; date = addDays(date, 1)) {
            if (dateFilter(date)) {
                return true;
            }
        }

        return false;
    }

    /**
     * Tests whether the combination month/year is after this.maxDate, considering
     * just the month and year of this.maxDate
     */
    private _isYearAndMonthAfterMaxDate(year: number, month: number): boolean {
        if (this.maxDate()) {
            const maxYear = getYear(this.maxDate());
            const maxMonth = getMonth(this.maxDate());

            return year > maxYear || (year === maxYear && month > maxMonth);
        }

        return false;
    }

    /**
     * Tests whether the combination month/year is before this.minDate, considering
     * just the month and year of this.minDate
     */
    private _isYearAndMonthBeforeMinDate(year: number, month: number): boolean {
        if (this.minDate()) {
            const minYear = getYear(this.minDate());
            const minMonth = getMonth(this.minDate());

            return year < minYear || (year === minYear && month < minMonth);
        }

        return false;
    }
}
